import type { ListBuilderColumn } from '../../types';

/**
 * Value mappings for entity type icons in task links
 */
const ENTITY_TYPE_ICON_MAPPING = [
	{
		value: 'issue',
		icon: 'triangle-exclamation',
		color: { themeColor: 'warning' },
	},
	{
		value: 'sourcing_request',
		icon: 'clipboard-list',
		color: { themeColor: 'statusInfo' },
	},
];

/**
 * Value mappings for lead/customer badges.
 *
 * Colors mirror the main client list (`libs/core/src/list-builder/configs/client/columns.ts`):
 *   Customer → blue  (#3B82F6)
 *   Lead     → slate (#64748B)  — neutral, not danger-red (PD-14529)
 *
 * Kept in sync across all lead/customer badges: client/columns.ts,
 * client/shared.ts, task/columns.ts, and license_match.config.ts.
 */
const LEAD_CUSTOMER_BADGE_MAPPING = [
	{
		value: 'Customer',
		displayText: 'C',
		color: { type: 'info' },
	},
	{
		value: 'Lead',
		displayText: 'L',
		color: { type: 'warning' },
	},
];

/**
 * Complete column - shows complete action dropdown.
 *
 * @deprecated Standard task lists now use {@link createStatusColumn} (an
 * inline To-Do / Done chip) for status changes. This helper is retained only
 * for legacy list configs that may still reference it.
 */
export const createCompleteColumn = (): ListBuilderColumn => ({
	id: 'complete-column',
	fields: [
		{
			id: 'complete-action',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			displayStyle: 'actions_dropdown',
			dropdownActions: [
				{
					id: 'complete-task-action',
					label: 'Complete',
					iconName: 'check',
					callbackName: 'complete',
					variant: 'success',
					visibility: 'visible',
				},
			],
		},
	],
	headers: [{ id: 'h-complete', text: '' }],
	width: 50,
	align: 'center',
	sortable: false,
});

/**
 * Status column - interactive To-Do / Done chip.
 *
 * Rendered by the `task-status-selector` custom component
 * (registered in `modules/tasks/src/components/list/registerTaskListComponents.ts`).
 * Mapping: `task.status === 'completed'` → "Done"; otherwise → "To-Do".
 */
export const createStatusColumn = (): ListBuilderColumn => ({
	id: 'status-column',
	fields: [
		{
			id: 'task-status-chip',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'status',
					objectTable: 'task',
				},
			},
			displayStyle: 'custom',
			customComponent: { id: 'task-status-selector' },
		},
	],
	headers: [{ id: 'h-status', text: 'Status' }],
	width: 110,
	align: 'center',
	sortable: true,
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'status',
			objectTable: 'task',
		},
	],
});

/**
 * Due date column with time formatting
 */
export const createDueDateColumn = (): ListBuilderColumn => ({
	id: 'due-date-column',
	fields: [
		{
			id: 'due-date',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'dueDate',
					objectTable: 'task',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
				formatOptions: {
					dateFormat: 'weekday-short-with-time',
					timezoneField: 'timezone',
					timeField: 'dueTime',
				},
			},
			displayStyle: 'date',
		},
	],
	headers: [{ id: 'h-due-date', text: 'Due Date' }],
	width: 130,
	sortable: true,
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'dueDate',
			objectTable: 'task',
		},
	],
});

/**
 * Assigned user column
 */
export const createAssignedUserColumn = (): ListBuilderColumn => ({
	id: 'assigned-user-column',
	fields: [
		{
			id: 'assigned-user-name',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'assignedUserName',
					objectTable: 'task',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h-assigned', text: 'Assigned To' }],
	width: 120,
	sortable: true,
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'assignedUserName',
			objectTable: 'task',
		},
		{
			direction: 'ASC',
			fieldName: 'dueDate',
			objectTable: 'task',
		},
	],
});

/**
 * Related-To column - shows the entity the task is linked to plus that
 * entity's client context (address, lead/customer chip).
 *
 * Layout for a task linked directly to a client:
 *   [C] Client Name
 *       Street Address
 *       City, State Zip
 *
 * Layout for a task linked to a non-client entity (issue / sourcing-request)
 * that is itself associated with a client:
 *   [icon] Issue Name
 *   [C]    Related Client Name
 *          Street Address
 *          City, State Zip
 *
 * Layout for an unlinked task:
 *   Not Linked
 *
 * Every secondary-line / address-line field uses `conditionalVisibility` to
 * hide when its underlying data is null. Without this the list-builder
 * defaults empty cells to "-" (see FieldRenderer.getFieldValue), which left
 * orphan dashes in the cell. The matching `line_break` is hidden too so the
 * row doesn't keep an empty line above the address.
 */
export const createLinksColumn = (): ListBuilderColumn => ({
	id: 'links-column',
	fields: [
		{
			id: 'entity-type-icon',
			floatingLeft: true,
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'linkEntityType',
					objectTable: 'task',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'value-mapping' },
				valueMapping: ENTITY_TYPE_ICON_MAPPING,
			},
			displayStyle: 'icon',
		},
		{
			id: 'lead-customer-badge-primary',
			floatingLeft: true,
			// Render only when the linked entity itself has a Lead/Customer flag
			// (i.e. when it's a client). Non-client entities leave the floating
			// badge slot empty.
			conditionalVisibility: {
				field: 'linkLeadOrCustomer',
				condition: 'is_not_null',
			},
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'linkLeadOrCustomer',
					objectTable: 'task',
				},
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'value-mapping' },
				valueMapping: LEAD_CUSTOMER_BADGE_MAPPING,
			},
			displayStyle: 'badge',
		},
		{
			id: 'link-name',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'linkName',
					objectTable: 'task',
				},
			},
			link: {
				type: 'custom',
				enabled: true,
				urlTemplate: '{linkUrl}',
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'statusInfo',
				},
			},
			displayStyle: 'text',
		},
		// Secondary line — only for non-client entities (issue / opp / SR) that
		// happen to be linked to a client. We gate everything on linkClientName.
		{
			id: 'line-break-1',
			conditionalVisibility: {
				field: 'linkClientName',
				condition: 'is_not_null',
			},
			dataConfig: { type: 'custom_text', customText: '' },
			displayStyle: 'line_break',
		},
		{
			id: 'lead-customer-badge-secondary',
			floatingLeft: true,
			conditionalVisibility: {
				field: 'linkClientName',
				condition: 'is_not_null',
			},
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'linkClientLeadOrCustomer',
					objectTable: 'task',
				},
			},
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'value-mapping' },
				valueMapping: LEAD_CUSTOMER_BADGE_MAPPING,
			},
			displayStyle: 'badge',
		},
		{
			id: 'link-client-name',
			conditionalVisibility: {
				field: 'linkClientName',
				condition: 'is_not_null',
			},
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'linkClientName',
					objectTable: 'task',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'text',
		},
		// Empty floating "badge" — renders as a 4px-wide inline-block span
		// (see BadgeDisplay.isLayoutSpacer). It widens the floating column by
		// a few pixels so the address lines below sit a comfortable distance
		// from the leading C/L chip rather than butting up against it.
		// This mirrors the spacer pair in the Licenses Pending Match Client
		// column. Removing these collapses the gap to the shared `gap-0.5`
		// only, which looks too tight without the previously-rendered "-".
		{
			id: 'address-badge-spacer-1',
			floatingLeft: true,
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'default' },
			},
			displayStyle: 'badge',
		},
		{
			id: 'line-break-2',
			conditionalVisibility: {
				field: 'linkStreetAddress',
				condition: 'is_not_null',
			},
			dataConfig: { type: 'custom_text', customText: '' },
			displayStyle: 'line_break',
		},
		{
			id: 'link-street-address',
			conditionalVisibility: {
				field: 'linkStreetAddress',
				condition: 'is_not_null',
			},
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'linkStreetAddress',
					objectTable: 'task',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'text',
		},
		{
			id: 'address-badge-spacer-2',
			floatingLeft: true,
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: {
				size: 'xs',
				colorRule: { type: 'default' },
			},
			displayStyle: 'badge',
		},
		{
			id: 'line-break-3',
			conditionalVisibility: {
				field: 'linkCityStateZip',
				condition: 'is_not_null',
			},
			dataConfig: { type: 'custom_text', customText: '' },
			displayStyle: 'line_break',
		},
		{
			id: 'link-city-state-zip',
			conditionalVisibility: {
				field: 'linkCityStateZip',
				condition: 'is_not_null',
			},
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'linkCityStateZip',
					objectTable: 'task',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
			},
			displayStyle: 'text',
		},
	],
	headers: [{ id: 'h-links', text: 'Related To' }],
	width: 220,
	sortable: false,
});

/**
 * Subject column with title and description
 */
export const createSubjectColumn = (): ListBuilderColumn => ({
	id: 'subject-column',
	fields: [
		{
			id: 'task-subject',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'title',
					objectTable: 'task',
				},
			},
			styleConfig: {
				size: 'sm',
				weight: 'medium',
				colorRule: { type: 'default' },
			},
			displayStyle: 'text',
		},
		{
			id: 'line-break-subject',
			dataConfig: { type: 'custom_text', customText: '' },
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			displayStyle: 'line_break',
		},
		{
			id: 'task-description',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'description',
					objectTable: 'task',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: {
					type: 'theme',
					themeColor: 'textSecondary',
				},
				// PD-14278 — notes wrap to two lines instead of cutting off at 100
				// characters; anything longer opens in the expansion modal. Two
				// lines keeps rows even (one long note cannot take over the page)
				// while showing well over what the old character cap allowed.
				textExpansion: { enabled: true, maxLines: 2 },
			},
			displayStyle: 'text',
		},
	],
	headers: [
		{ id: 'h-subject', text: 'Subject' },
		{ id: 'h-description', text: 'Description' },
	],
	width: 250,
	sortable: false,
});

/**
 * Priority column - interactive Low / Medium / High chip.
 *
 * Rendered by the `task-priority-selector` custom component
 * (registered in `modules/tasks/src/components/list/registerTaskListComponents.ts`).
 * PRIORITY_VALUE_MAPPING is retained for read-only fallbacks elsewhere.
 */
export const createPriorityColumn = (): ListBuilderColumn => ({
	id: 'priority-column',
	fields: [
		{
			id: 'priority-chip',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'priority',
					objectTable: 'task',
				},
			},
			displayStyle: 'custom',
			customComponent: { id: 'task-priority-selector' },
		},
	],
	headers: [{ id: 'h-priority', text: 'Priority' }],
	width: 110,
	align: 'center',
	sortable: true,
	sortFields: [
		{
			direction: 'ASC',
			fieldName: 'priority',
			objectTable: 'task',
		},
	],
});

/**
 * Completed date column with date formatting
 */
export const createCompletedDateColumn = (): ListBuilderColumn => ({
	id: 'completed-date-column',
	fields: [
		{
			id: 'completed-date',
			dataConfig: {
				type: 'field',
				field: {
					fieldName: 'completedDate',
					objectTable: 'task',
				},
			},
			styleConfig: {
				size: 'sm',
				colorRule: { type: 'default' },
				formatOptions: {
					dateFormat: 'weekday-short-with-time',
					timezoneField: 'timezone',
				},
			},
			displayStyle: 'date',
		},
	],
	headers: [{ id: 'h-completed-date', text: 'Completed' }],
	width: 130,
	sortable: true,
	sortFields: [
		{
			direction: 'DESC',
			fieldName: 'completedDate',
			objectTable: 'task',
		},
	],
});

/**
 * Actions column with edit and delete
 */
export const createActionsColumn = (): ListBuilderColumn => ({
	id: 'actions-column',
	fields: [
		{
			id: 'task-actions',
			dataConfig: {
				type: 'custom_text',
				customText: '',
			},
			styleConfig: {
				size: 'md',
				colorRule: { type: 'default' },
			},
			displayStyle: 'actions_dropdown',
			dropdownActions: [
				{
					id: 'edit-action',
					label: 'Edit',
					iconName: 'edit',
					callbackName: 'edit',
					variant: 'primary',
					visibility: 'visible',
				},
				{
					id: 'delete-action',
					label: 'Delete',
					iconName: 'trash',
					callbackName: 'delete',
					variant: 'danger',
					visibility: 'visible',
				},
			],
		},
	],
	headers: [{ id: 'h-actions', text: '' }],
	width: 100,
	align: 'center',
	sortable: false,
});

/**
 * Creates the standard set of task columns (used by most lists).
 *
 * Order matches the Issues list pattern: identifying text first, then the
 * inline chips the user is most likely to mutate, then context, then actions.
 *   Subject → Status → Priority → Assigned To → Due Date → Related To → Actions.
 */
export const createStandardTaskColumns = (): Array<ListBuilderColumn> => [
	createSubjectColumn(),
	createStatusColumn(),
	createPriorityColumn(),
	createAssignedUserColumn(),
	createDueDateColumn(),
	createLinksColumn(),
	createActionsColumn(),
];

/**
 * Creates columns for the "Completed Tasks" list.
 *
 * Same column order as the standard list, but with an extra "Completed" date
 * column appended right before Actions, and the Status chip omitted since
 * every row is already Done (the user can still re-open via Edit).
 */
export const createCompletedTaskColumns = (): Array<ListBuilderColumn> => [
	createSubjectColumn(),
	createPriorityColumn(),
	createAssignedUserColumn(),
	createDueDateColumn(),
	createCompletedDateColumn(),
	createLinksColumn(),
	createActionsColumn(),
];
